import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { getClientProspects } from "@/app/actions/prospect-search/prospects/action";
import {
  PROSPECT_ACTION_FILTER_ALL,
  ProspectActionFilter,
  prospectActionFilterKey,
} from "@/app/types/prospect/ProspectActionFilter";
import { ProspectListTab } from "@/app/types/prospect/ProspectListTab";
import { Globals } from "@/app/vars/Globals";
import { QueryKey } from "@/app/vars/QueryKeys";

export function useFetchClientProspects(options: {
  avatarId?: number;
  page: number;
  enabled?: boolean;
  isRefreshing?: boolean;
  tab?: ProspectListTab;
  actionFilter?: ProspectActionFilter;
  refetchOnWindowFocus?: boolean;
}) {
  const {
    avatarId,
    page,
    enabled = true,
    isRefreshing = false,
    tab = ProspectListTab.ALL,
    actionFilter = PROSPECT_ACTION_FILTER_ALL,
    refetchOnWindowFocus = true,
  } = options;

  return useQuery({
    queryKey: [
      QueryKey.CLIENT_PROSPECTS,
      avatarId,
      tab,
      prospectActionFilterKey(actionFilter),
      page,
    ],
    queryFn: () => getClientProspects(avatarId!, page, tab, actionFilter),
    enabled: enabled && !!avatarId,
    // The extension delivers prospects out-of-band; refresh on tab focus, throttled
    // by staleTime so navigation stays cache-instant within the window.
    staleTime: Globals.PROSPECT_FRESHNESS_STALE_TIME_MS,
    refetchOnWindowFocus,
    // While an analysis is running, poll like the stats do so the list fills in
    // lockstep with the counter instead of staying stale until a reload.
    refetchInterval: isRefreshing ? Globals.PROSPECT_JOB_POLLING_INTERVAL_MS : false,
    // Keep the current page visible while the next one loads so paginating
    // never drops back to a loading state (isLoading stays true only on the
    // very first load, which is what gates the page-level loader).
    placeholderData: keepPreviousData,
  });
}
